import {
  $applyNodeReplacement,
  EditorConfig,
  LexicalNode,
  LexicalUpdateJSON,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';

import { InlineStyleElementNode } from '../inlineStyle';
import { HIGHLIGHT_COLORS, HighlightColor } from './highlightConstants';

export type SerializedHighlightNode = Spread<
  { highlightColor: HighlightColor },
  SerializedElementNode
>;

export class HighlightNode extends InlineStyleElementNode {
  __highlightColor: HighlightColor;

  static override getType(): string {
    return 'highlight-mark';
  }

  static override clone(node: HighlightNode): HighlightNode {
    return new HighlightNode(node.__highlightColor, node.__key);
  }

  constructor(color: HighlightColor, key?: NodeKey) {
    super(key);
    this.__highlightColor = color;
  }

  override createDOM(_config: EditorConfig): HTMLElement {
    const element = document.createElement('mark');
    element.style.backgroundColor = HIGHLIGHT_COLORS[this.__highlightColor];
    element.dataset.highlightColor = this.__highlightColor;
    return element;
  }

  override updateDOM(
    prevNode: HighlightNode,
    element: HTMLElement,
    _config: EditorConfig
  ): boolean {
    if (prevNode.__highlightColor !== this.__highlightColor) {
      element.style.backgroundColor = HIGHLIGHT_COLORS[this.__highlightColor];
      element.dataset.highlightColor = this.__highlightColor;
    }
    return false;
  }

  getColor(): HighlightColor {
    return this.getLatest().__highlightColor;
  }

  setColor(color: HighlightColor): this {
    const self = this.getWritable();
    self.__highlightColor = color;
    return self;
  }

  static override importJSON(
    serializedNode: SerializedHighlightNode
  ): HighlightNode {
    // Chain through updateFromJSON so ElementNode-level properties (format,
    // indent, direction, textFormat, textStyle) are restored from the
    // serialized payload.
    return $createHighlightNode(serializedNode.highlightColor).updateFromJSON(
      serializedNode
    );
  }

  override updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedHighlightNode>
  ): this {
    return super
      .updateFromJSON(serializedNode)
      .setColor(serializedNode.highlightColor);
  }

  override exportJSON(): SerializedHighlightNode {
    return {
      ...super.exportJSON(),
      highlightColor: this.__highlightColor,
      type: HighlightNode.getType(),
      version: 1,
    };
  }
}

export function $createHighlightNode(color: HighlightColor): HighlightNode {
  return $applyNodeReplacement(new HighlightNode(color));
}

export function $isHighlightNode(
  node: LexicalNode | null | undefined
): node is HighlightNode {
  return node instanceof HighlightNode;
}
